/**
 * stageBar.ts — the pure half of the deal page's stage bar (deal detail slice
 * 1, §39.9; spec §3.3): the five steps, each step's state against the deal's
 * stage, the next open stage the primary button offers, and the time a deal
 * spent in a step read from the EXISTING stage-events feed
 * (GET /v1/sales/deals/{id}/stage-events — no new data).
 *
 * The red "more than 2× the won-deal median" colour is R6's, decided in
 * the server's assessment (deal.assessment.stage, §40.12 rule set 2) and passed to the bar as data.
 */
import type { DealStage } from '../../types'
import type { SalesDealStageEvent } from '../../../../api/types'
import { SLUG_TO_STAGE, STAGE_TO_SLUG } from '../../api'

/** The five steps, in order. Closed Lost is an exit, not a step (the mockup's
 *  funnel, SalesDealDetail's FUNNEL since v1). */
export const STAGE_STEPS: readonly DealStage[] = ['New Lead', 'Qualified', 'Proposal Sent', 'Negotiation', 'Closed Won']

export type StepState = 'past' | 'current' | 'future'

/** A step's state for a deal in `stage`. A lost or unknown deal is in no step:
 *  every step reads future and the page says why beside the bar. */
export function stepState(step: DealStage, stage: DealStage): StepState {
  const cur = STAGE_STEPS.indexOf(stage)
  const i = STAGE_STEPS.indexOf(step)
  if (cur < 0 || i < 0) return 'future'
  return i < cur ? 'past' : i === cur ? 'current' : 'future'
}

/** The open stage "Move to …" offers, or null: Negotiation's next step is the
 *  close ceremony ("Mark won"), and a closed or unknown deal moves nowhere. */
export function nextOpenStage(stage: DealStage): DealStage | null {
  const i = STAGE_STEPS.indexOf(stage)
  if (i < 0 || i >= 3) return null
  return STAGE_STEPS[i + 1]
}

function byTime(events: readonly SalesDealStageEvent[]): SalesDealStageEvent[] {
  return [...events].sort((a, b) => Date.parse(a.changedAt) - Date.parse(b.changedAt))
}

/**
 * Whole days the deal spent in `step`, summed over every visit, from the
 * stage events (either wire order in). A visit starts at the event that
 * ENTERED the step (`toStage`) and ends at the next event; the latest visit
 * with no later event runs to `now`. null = the events never show the deal
 * entering the step (born later, or history the read does not hold): "no
 * time", never a fabricated 0.
 */
export function daysInStep(events: readonly SalesDealStageEvent[], step: DealStage, now: number): number | null {
  const slug = STAGE_TO_SLUG[step]
  if (!slug) return null
  const sorted = byTime(events)
  let total = 0
  let seen = false
  for (let i = 0; i < sorted.length; i++) {
    if (sorted[i].toStage !== slug) continue
    seen = true
    const start = Date.parse(sorted[i].changedAt)
    const end = i + 1 < sorted.length ? Date.parse(sorted[i + 1].changedAt) : now
    if (Number.isFinite(start) && Number.isFinite(end) && end > start) total += (end - start) / 86_400_000
  }
  return seen ? Math.floor(total) : null
}

/** The stage the deal was born in — the birth event, the one with no
 *  `fromStage` — or null when the events do not hold it. */
export function birthStage(events: readonly SalesDealStageEvent[]): DealStage | null {
  const birth = byTime(events).find(e => !e.fromStage)
  return birth ? (SLUG_TO_STAGE[birth.toStage] ?? null) : null
}

/** Days between the move INTO `event` and the event before it — "After 12
 *  days in Qualified"; null when there is no earlier event to measure from. */
export function daysBeforeMove(events: readonly SalesDealStageEvent[], eventId: string): number | null {
  const sorted = byTime(events)
  const i = sorted.findIndex(e => e.id === eventId)
  if (i <= 0) return null
  const ms = Date.parse(sorted[i].changedAt) - Date.parse(sorted[i - 1].changedAt)
  return Number.isFinite(ms) && ms >= 0 ? Math.floor(ms / 86_400_000) : null
}

export function daysLabel(days: number): string {
  return days === 1 ? '1 day' : `${days} days`
}
